import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const ZONES = [["Asia/Kolkata", "IST"], ["America/Los_Angeles", "PST"], ["Asia/Dubai", "GST"], ["UTC", "UTC"]];
const RECIPIENT_RULES = {
  request_submitted: "Team Lead/approver (then superior → Super Admin on escalation)",
  request_decided: "The employee",
  request_escalated: "Approver's superior, then Super Admin",
  announcement: "Selected audience only",
  task_assigned: "Assignee",
  assignment_change: "Employee, their Team Lead and HR",
  late_login: "Employee and their Team Lead",
  break_limit: "Super Admin and that team's Lead only",
  break_ending: "Employee (desktop/in-app)",
  attendance: "Employee",
  weekly_report: "Super Admin/Founder (all) and each Team Lead (own team)",
};
const save = async (fn, msg) => {
  try {
    await fn();
    toast.success(msg);
  } catch (e) {
    toast.error(apiErr(e));
  }
};

function WeeklyReport() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/notify/weekly-report/settings").then(({ data }) => setS(data)).catch(() => {}); }, []);
  if (!s) return null;
  return (
    <Card className="space-y-3 p-5" data-testid="weekly-report-settings">
      <div className="flex items-center justify-between font-semibold">Weekly HR report
        <Switch checked={s.enabled} onCheckedChange={(v) => setS({ ...s, enabled: v })} data-testid="weekly-enabled" />
      </div>
      <div className="grid gap-3 sm:grid-cols-3">
        <div><Label>Day</Label>
          <Select value={String(s.day)} onValueChange={(v) => setS({ ...s, day: Number(v) })}>
            <SelectTrigger data-testid="weekly-day"><SelectValue /></SelectTrigger>
            <SelectContent>{DAYS.map((d, i) => <SelectItem key={d} value={String(i)}>{d}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label>Time</Label><Input type="time" value={s.time} onChange={(e) => setS({ ...s, time: e.target.value })} data-testid="weekly-time" /></div>
        <div><Label>Timezone</Label>
          <Select value={s.timezone} onValueChange={(v) => setS({ ...s, timezone: v })}>
            <SelectTrigger data-testid="weekly-timezone"><SelectValue /></SelectTrigger>
            <SelectContent>{ZONES.map(([z, l]) => <SelectItem key={z} value={z}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div className="text-xs text-muted-foreground">Last sent: {s.last_sent_key || "never"}. Super Admin/Founder get the full report; each Team Lead gets their own team only.</div>
      <div className="flex gap-2">
        <Button size="sm" className="bg-[#255058] text-white hover:bg-[#1c3d43]" data-testid="weekly-save"
          onClick={() => save(() => api.put("/notify/weekly-report/settings", { enabled: s.enabled, day: s.day, time: s.time, timezone: s.timezone }), "Weekly schedule saved")}>Save schedule</Button>
        <Button size="sm" variant="outline" data-testid="weekly-send-now"
          onClick={() => save(() => api.post("/notify/weekly-report/send-now"), "Weekly report queued")}>Send now</Button>
      </div>
    </Card>
  );
}

function EventRules() {
  const [events, setEvents] = useState(null);
  useEffect(() => { api.get("/notify/rules").then(({ data }) => setEvents(data.events)).catch(() => {}); }, []);
  if (!events) return null;
  const set = (key, patch) => setEvents({ ...events, [key]: { ...events[key], ...patch } });
  return (
    <Card className="p-5" data-testid="notification-rules">
      <div className="font-semibold">Email recipients per event</div>
      <div className="text-xs text-muted-foreground">Core recipients are enforced by the server. Add extra CC addresses (comma separated) or switch an event off.</div>
      <div className="mt-3 space-y-2">
        {Object.entries(events).map(([key, rule]) => (
          <div key={key} className="grid items-center gap-2 rounded-md border border-border p-2 md:grid-cols-[1fr_1.4fr_auto]">
            <div><div className="text-sm font-medium capitalize">{key.replace(/_/g, " ")}</div><div className="text-xs text-muted-foreground">{RECIPIENT_RULES[key]}</div></div>
            <Input placeholder="cc@company.com" value={(rule.cc || []).join(", ")} data-testid={`rule-cc-${key}`}
              onChange={(e) => set(key, { cc: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })} />
            <Switch checked={rule.enabled} onCheckedChange={(v) => set(key, { enabled: v })} data-testid={`rule-enabled-${key}`} />
          </div>
        ))}
      </div>
      <Button size="sm" className="mt-3 bg-[#255058] text-white hover:bg-[#1c3d43]" data-testid="rules-save"
        onClick={() => save(() => api.put("/notify/rules", { events }), "Recipient rules saved")}>Save rules</Button>
    </Card>
  );
}

function DeliveryLog() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/notify/delivery-log").then(({ data }) => setItems(data.items)).catch(() => {});
  useEffect(() => { load(); }, []);
  return (
    <Card className="p-5" data-testid="email-delivery-log">
      <div className="flex items-center justify-between font-semibold">Email delivery log
        <Button size="sm" variant="outline" onClick={() => save(() => api.post("/notify/delivery-log/retry").then(load), "Retry started")} data-testid="delivery-retry">Retry failed</Button>
      </div>
      <div className="mt-3 max-h-80 divide-y divide-border overflow-y-auto text-xs">
        {items.map((i) => (
          <div key={i.id} className="flex justify-between gap-3 py-1.5">
            <span className="truncate"><b>{i.to}</b> · {i.subject}</span>
            <span className={`shrink-0 ${i.status === "failed" ? "text-red-600" : i.status === "sent" ? "text-emerald-600" : "text-muted-foreground"}`}>
              {i.status}{i.attempts > 1 ? ` (${i.attempts})` : ""} · {new Date(i.created_at).toLocaleString()}
            </span>
          </div>
        ))}
        {!items.length && <div className="py-6 text-center text-muted-foreground">No emails queued yet.</div>}
      </div>
    </Card>
  );
}

export function HrmsEmailSettings() {
  return (
    <div className="mt-6 grid gap-4 xl:grid-cols-2">
      <WeeklyReport />
      <DeliveryLog />
      <div className="xl:col-span-2"><EventRules /></div>
    </div>
  );
}
